import { useEffect, useState, useRef } from 'react'
import { View, Text, ActivityIndicator, StyleSheet } from 'react-native'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { storage, TOKEN_KEY, USER_ID_KEY, HAS_LOGGED_IN_KEY } from '../../src/utils/storage'
import { apiClient } from '../../src/api/client'
import { useAuth } from '../../src/auth/AuthProvider'
import { colors, typography } from '../../src/theme'
import { identifyUser, events } from '../../src/lib/posthog'
import { toast } from '../../src/components/Toast'

/**
 * MitID OAuth callback handler.
 * Web: receives ?code=...&state=... from URL redirect
 * Native: handled by WebBrowser.openAuthSessionAsync in AuthProvider
 *
 * Matches Next.js frontend/src/app/mitid/callback/page.tsx:
 * - AbortController for cleanup
 * - User name in toast messages
 * - Same error handling flow
 */
export default function CallbackScreen() {
  const router = useRouter()
  const params = useLocalSearchParams<{ code?: string; state?: string; error?: string; error_description?: string }>()
  const { refreshUser, setUserDirectly } = useAuth()
  const [status, setStatus] = useState<string>('Behandler MitID login...')
  const [errorState, setErrorState] = useState<string | null>(null)
  const abortedRef = useRef(false)

  useEffect(() => {
    // AbortController equivalent for cleanup (matches Next.js)
    abortedRef.current = false

    const completeMitID = async () => {
      const { code, state, error, error_description } = params

      // Handle MitID error params
      if (error) {
        const errorMsg = error_description || error
        setErrorState(`MitID fejl: ${errorMsg}`)
        setStatus(`Fejl: ${errorMsg}`)
        toast.error(`MitID fejl: ${errorMsg}`)
        setTimeout(() => router.replace('/(auth)/login'), 3000)
        return
      }

      if (!code || !state) {
        setErrorState('Manglende parametre fra MitID')
        setStatus('Fejl: Manglende parametre fra MitID')
        toast.error('Ugyldigt MitID svar')
        setTimeout(() => router.replace('/(auth)/login'), 2000)
        return
      }

      try {
        setStatus('Logger ind...')
        const res = await apiClient.auth.callback(code, state)

        // If component unmounted / effect cleaned up, stop
        if (abortedRef.current) return

        const { access_token, user_id, is_new_user, onboarding_completed, full_name } = res.data

        // Store JWT and user ID
        await storage.set(TOKEN_KEY, access_token)
        await storage.set(USER_ID_KEY, user_id)
        await storage.set(HAS_LOGGED_IN_KEY, '1')

        // Set user state IMMEDIATELY from callback data to prevent auth guard race condition.
        // Without this, the (tabs) layout may see isAuthenticated=false before refreshUser()
        // completes, causing a redirect back to login (double MitID prompt).
        setUserDirectly({
          id: user_id,
          email: '',
          full_name: full_name || null,
          cpr_number: null,
          mitid_verified: true,
          onboarding_completed,
          fuldmagt_accepted: false,
          consent_given: false,
        })

        // Track login in PostHog
        identifyUser(user_id, { name: full_name, is_new_user, onboarding_completed })
        events.loginCompleted('mitid')

        // Route based on onboarding status
        if (is_new_user || !onboarding_completed) {
          toast.success(`Velkommen${is_new_user ? '' : ' tilbage'}, ${full_name || 'ny bruger'}!`)
          router.replace('/onboarding')
        } else {
          toast.success(`Velkommen tilbage, ${full_name || ''}!`)
          router.replace('/(tabs)')
        }

        // Fetch full user profile in background (non-blocking) for complete data
        refreshUser().catch(() => {})
      } catch (error: any) {
        // If aborted (cleanup), ignore the error
        if (abortedRef.current) return
        const errorMsg = error?.response?.data?.detail || 'Der skete en fejl under login'
        console.error('Callback failed:', error)
        setErrorState(errorMsg)
        setStatus(`Fejl: ${errorMsg}`)
        toast.error(errorMsg)
        setTimeout(() => router.replace('/(auth)/login'), 3000)
      }
    }

    completeMitID()

    // Cleanup: mark as aborted (equivalent to AbortController.abort in Next.js)
    return () => {
      abortedRef.current = true
    }
  }, [params.code, params.state])

  return (
    <View style={styles.container}>
      {errorState ? (
        <>
          <View style={styles.errorIcon}>
            <Text style={styles.errorIconText}>!</Text>
          </View>
          <Text style={styles.title}>MitID Login</Text>
          <Text style={styles.errorMessage}>{errorState}</Text>
          <Text style={styles.redirectText}>Du vil blive omdirigeret automatisk...</Text>
        </>
      ) : (
        <>
          <ActivityIndicator size="large" color={colors.primary} style={{ marginBottom: 16 }} />
          <Text style={styles.title}>MitID Login</Text>
          <Text style={styles.statusText}>{status}</Text>
          <Text style={styles.redirectText}>Du vil blive omdirigeret automatisk...</Text>
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
    gap: 12,
    paddingHorizontal: 24,
  },
  title: {
    ...typography.h2,
    color: colors.text.primary,
  },
  statusText: {
    ...typography.body,
    color: colors.text.secondary,
  },
  errorIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fef2f2',
    borderWidth: 2,
    borderColor: '#fecaca',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  errorIconText: {
    fontSize: 24,
    fontWeight: '700',
    color: '#dc2626',
  },
  errorMessage: {
    ...typography.body,
    color: '#dc2626',
    textAlign: 'center',
  },
  redirectText: {
    ...typography.caption,
    color: colors.text.muted,
    marginTop: 8,
  },
})
